import type { Vane } from '@/types';

/**
 * Vane catalog — cross-checked against current manufacturer sites + Lancaster
 * Archery Supply published specs (last verified May 2026).
 *
 * Every entry below carries an inline "(verified)" comment block per brand;
 * the few that are missing are noted as such. When in doubt, the verifying
 * source for vane weight + length is the manufacturer's product page — vane
 * weight varies <5% across batches so the catalog values are tolerance-good
 * for FOC and total-arrow-weight calculations.
 *
 * Weight = single-vane mass in grains.
 *
 * OPTIONAL study fields (added 2026-07-20, additive/permalink-safe): where an
 * inline comment already carried a 2025/2026 Yates/PCA finding, that finding is
 * now also machine-readable via `noiseTier` / `steerTier` / `studyNote` (see the
 * Vane type). Only entries whose comment states a finding carry them; the comment
 * is kept as provenance. `studyNote` is present whenever any tier is set.
 */
export const VANES: readonly Vane[] = [
  // ────────────────────────────────────────────────────────
  // AAE — verified from aaearchery.com + Lancaster
  // ────────────────────────────────────────────────────────
  // Hybrid series (verified)
  { name: 'AAE Hybrid 16',            weight: 4.0,  length: '1.7"'  },
  { name: 'AAE Hybrid 1.85 Shield',   weight: 3.2,  length: '1.85"' },
  { name: 'AAE Hybrid 2.0 Shield',    weight: 3.9,  length: '1.95"' },
  { name: 'AAE Hybrid HP',            weight: 6.2,  length: '2.0"'  },
  { name: 'AAE Hybrid 23',            weight: 5.7,  length: '2.3"'  },
  { name: 'AAE Hybrid 26',            weight: 8.3,  length: '2.7"'  },
  { name: 'AAE Hybrid 4.0',           weight: 10.6, length: '3.8"'  },
  // Max series (verified)
  { name: 'AAE Max Hunter',           weight: 7.4,  length: '2.1"'  },
  { name: 'AAE Max Stealth',          weight: 8.8,  length: '2.8"'  },
  // Plastifletch Max series (Lancaster-verified)
  { name: 'AAE Plastifletch Max 2.0', weight: 3.9,  length: '2.0"'  },
  { name: 'AAE Plastifletch Max 23',  weight: 4.7,  length: '2.3"'  },
  // Pro series (legacy / current)
  { name: 'AAE Pro Stealth',          weight: 7.0,  length: '2.1"'  },
  { name: 'AAE Pro Max',              weight: 4.9,  length: '1.7"'  },
  // EP target series
  { name: 'AAE EP23',                 weight: 4.9,  length: '2.3"'  },
  { name: 'AAE EP26',                 weight: 7.0,  length: '2.6"'  },
  { name: 'AAE EP40',                 weight: 10.3, length: '3.80"' },
  // Trad
  { name: 'AAE Trad Vane',            weight: 6.0,  length: '2.0"'  },

  // ────────────────────────────────────────────────────────
  // ULTRAVIEW — verified from ultraviewarchery.com (May 2026).
  // UV Vane is injection-molded (not ribbon-cut) with debossed pattern for
  // aerodynamic efficiency. Single size — used on UV 1K arrow system. Quiet.
  // ────────────────────────────────────────────────────────
  { name: 'Ultraview UV Vane',        weight: 8.0,  length: '2.85"' },  // VERIFIED 0.46" H, sold 40pk

  // ────────────────────────────────────────────────────────
  // BOHNING — verified from shop.bohning.com
  // ────────────────────────────────────────────────────────
  { name: 'Bohning Blazer X2',        weight: 4.0,  length: '1.85"' },  // VERIFIED
  { name: 'Bohning Blazer',           weight: 6.1,  length: '2.0"'  },  // VERIFIED
  { name: 'Bohning X Vane 1.5"',      weight: 3.3,  length: '1.5"'  },  // VERIFIED 1.5"/0.35"H/3.3gr (weight corrected from 4.0 — 2026-06-19)
  { name: 'Bohning X Vane 1.75"',     weight: 4.5,  length: '1.75"' },
  { name: 'Bohning X Vane 2.25"',     weight: 5.8,  length: '2.25"' },  // VERIFIED
  { name: 'Bohning X Vane 3.0"',      weight: 10.0, length: '3.0"'  },  // VERIFIED (Bohning makes 3", NOT 4")
  { name: 'Bohning X3 Vane 2.25"',    weight: 6.0,  length: '2.25"' },
  { name: 'Bohning Heat',             weight: 6.1,  length: '2.5"'  },  // VERIFIED 2.5"/0.41"H/6.1gr from bohning.com (CORRECTED from 2.0"/7.0 — dealer-flagged 2026-06-19)
  { name: 'Bohning Killer Vane',      weight: 12.5, length: '4.0"'  },  // VERIFIED 4.0"/0.5"H/12.5gr (Killer is sold today as "Bronco"; CORRECTED from 3.0"/9.0 — 2026-06-19)
  { name: 'Bohning Air Vane',         weight: 4.5,  length: '2.0"'  },
  { name: 'Bohning Trad Vane 4"',     weight: 5.5,  length: '4.0"'  },

  // ────────────────────────────────────────────────────────
  // DCA CUSTOM ARROWS — verified from dcacustomarrows.com (Patent 11,686,562)
  // ────────────────────────────────────────────────────────
  { name: 'DCA Super Sabre Rev 2.0',  weight: 7.1,  length: '2.25"' },  // VERIFIED
  { name: 'DCA Mini Sabre',           weight: 6.0,  length: '2.0"'  },  // VERIFIED

  // ────────────────────────────────────────────────────────
  // Q2i — verified from q2iarchery.com
  // Q2i sizes are 1.5"/1.75"/2.1"/2.5"/3.0"/3.5"/4.0" (NOT 2.7")
  // ────────────────────────────────────────────────────────
  { name: 'Q2i Fusion X-II 2.1"',     weight: 5.8,  length: '2.1"'  },  // VERIFIED
  { name: 'Q2i Fusion-II 2.1"',       weight: 7.5,  length: '2.1"'  },  // VERIFIED
  { name: 'Q2i RAPT-X 2.0"',          weight: 7.5,  length: '2.0"'  },  // VERIFIED
  { name: 'Q2i Fusion X-II 1.75"',    weight: 4.5,  length: '1.75"' },
  { name: 'Q2i Fusion X-II 2.5"',     weight: 8.3,  length: '2.5"'  },
  { name: 'Q2i Fusion X-II 3.0"',     weight: 9.5,  length: '3.0"'  },
  { name: 'Q2i Fusion X-II 4.0"',     weight: 12.5, length: '4.0"'  },
  { name: 'Q2i Snipe',                weight: 4.5,  length: '1.75"' },

  // ────────────────────────────────────────────────────────
  // AAE — additional study-mentioned vanes (PCA/Yates 2025)
  // ────────────────────────────────────────────────────────
  { name: 'AAE Max 23',                weight: 6.0,  length: '2.3"', steerTier: 'strong', studyNote: '2025 Yates/PCA study: best 4-fletch ROI — +5.8% drag for −10.2% drift.' },  // 2025 study: best 4F ROI (+5.8% drag for -10.2% drift)
  { name: 'AAE Airazr 26',             weight: 7.0,  length: '2.8"', steerTier: 'moderate', studyNote: 'Yates study: lift-leaner cluster.' },  // Yates lift-leaner cluster
  { name: 'AAE Airazr 23',             weight: 5.0,  length: '2.3"', steerTier: 'strong', studyNote: '2026 study standout — strong steer from modest vane height.' },  // 2026 study standout (strong steer from modest height); 2.3"x.38" verified, wt est. (Hybrid 23 = 5.7gr, Airazr lighter)
  { name: 'AAE Airazr Talon 3.0',      weight: 8.8,  length: '3.0"', steerTier: 'strong', studyNote: 'Yates study: best lift recovery (−6.4" vs mean).' },  // Yates: best lift recovery (-6.4" vs mean)
  { name: 'AAE Talon',                 weight: 7.2,  length: '2.5"', steerTier: 'moderate', studyNote: 'Yates study: lift-leaner cluster.' },  // Yates lift-leaner cluster

  // ────────────────────────────────────────────────────────
  // FLEX-FLETCH — verified from flexfletch.com (study-mentioned models)
  // ────────────────────────────────────────────────────────
  { name: 'Flex-Fletch 360',           weight: 7.5,  length: '3.6"', noiseTier: 'quiet', studyNote: 'Yates 2025 study: QUIETEST vane tested.' },  // VERIFIED — Yates: QUIETEST in 2025 study
  { name: 'Flex-Fletch Silent Knight 2.0"', weight: 4.6, length: '2.0"', steerTier: 'strong', studyNote: 'Yates study: dark-green elite (top ROI) tier.' },  // Yates dark-green elite tier
  { name: 'Flex-Fletch Silent Knight 2.18"', weight: 6.8, length: '2.18"' },
  { name: 'Flex-Fletch FFP-218',       weight: 6.5,  length: '2.18"' },
  { name: 'Flex-Fletch FFP-225',       weight: 3.75, length: '2.25"', steerTier: 'strong', studyNote: '2026 study standout — strong steer from low height/weight.' },  // 2026 study standout (low-height/-weight steer); 2.25" verified, wt est. from FFP-218 sibling
  { name: 'Flex-Fletch FFP-300',       weight: 6.1,  length: '3.0"', noiseTier: 'quiet', steerTier: 'strong', studyNote: '2026 study: best QUIET steering in 4-fletch.' },  // VERIFIED flexfletch.com 3.0"/0.34"H/6.1gr — 2026 study: best QUIET steering (4-fletch)
  { name: 'Flex-Fletch Quad X',        weight: 5.0,  length: '2.2"', steerTier: 'weak', studyNote: '2025 study: 4-fletch ROI was NEGATIVE (+3% drift).' },  // 2025 study: 4-fletch ROI was NEGATIVE (+3% drift)
  { name: 'Flex-Fletch Quad-X',        weight: 5.0,  length: '2.3"'  },

  // ────────────────────────────────────────────────────────
  // TAC VANES — verified from floutdoors.com (official TAC home, 2026-06-13)
  // Full catalog audit after customer flag (Cody) re: missing DHP entries.
  // Prior 1.85 and 3.0 entries were fabricated — TAC actually sells 1.75 and 3.75.
  // ────────────────────────────────────────────────────────
  // Driver series — low-to-mid profile, all-purpose
  { name: 'TAC Driver 1.75',           weight: 3.0,  length: '1.75"' },  // Ultra-low profile, light-fast 3D/target
  { name: 'TAC Driver 2.00',           weight: 4.0,  length: '2.00"' },  // Low profile
  { name: 'TAC Driver 2.25',           weight: 5.0,  length: '2.25"' },  // Mid profile, hunter all-around
  { name: 'TAC Driver 2.75',           weight: 6.2,  length: '2.75"' },  // Mid profile, balanced hunting / 3D
  { name: 'TAC Driver 3.75',           weight: 7.6,  length: '3.75"' },  // Mid profile, indoor target / long-range stability
  // Driver High Profile — broadhead-stabilizing variant (2.25" x .515" tall)
  { name: 'TAC Driver High Profile 2.25', weight: 5.0, length: '2.25"' }, // High profile, peak broadhead control
  // Matrix series — low-to-mid profile, fits any shaft diameter
  { name: 'TAC Matrix 1.75',           weight: 3.6,  length: '1.75"' },  // Low profile, light-fast
  { name: 'TAC Matrix 2.00',           weight: 4.8,  length: '2.00"' },  // Low profile, 3D / target
  { name: 'TAC Matrix 2.25',           weight: 5.6,  length: '2.25"' },  // Mid profile, hunter all-around
  // Summit series — high profile, compact hunter vane
  { name: 'TAC Summit 1.95',           weight: 4.8,  length: '1.95"' },  // High profile, hunter / 3D

  // ────────────────────────────────────────────────────────
  // Q2i — additional study models
  // ────────────────────────────────────────────────────────
  { name: 'Q2i Griff X 30',            weight: 8.85, length: '3.0"', steerTier: 'moderate', studyNote: 'Yates study: lift-leaner, balanced all-around tier.' },  // Yates lift-leaner; balanced tier all-around

  // ────────────────────────────────────────────────────────
  // EASTON
  // ────────────────────────────────────────────────────────
  { name: 'Easton Diamond 3"',         weight: 8.0,  length: '3.0"' },
  { name: 'Easton Diamond 4"',         weight: 9.0,  length: '4.0"' },
  { name: 'Easton Hex Hunter',         weight: 7.5,  length: '2.7"' },
  { name: 'Easton Vector',             weight: 7.0,  length: '2.0"' },
  { name: 'Easton Hybrid Stealth (5.0 MG stock)', weight: 7.5, length: '3.0"' },

  // ────────────────────────────────────────────────────────
  // GOLD TIP
  // ────────────────────────────────────────────────────────
  { name: 'Gold Tip Tec Vane 2.5"',          weight: 7.0,  length: '2.5"'   },
  { name: 'Gold Tip Fusion Vane 2.71"',      weight: 7.5,  length: '2.71"'  },
  { name: 'Gold Tip Fusion X 2.1"',          weight: 7.0,  length: '2.1"'   },
  { name: 'Gold Tip Rapt-X 2.0"',            weight: 7.5,  length: '2.0"'   },
  { name: 'Gold Tip 4 Fletch Vane 2.75"',    weight: 6.0,  length: '2.75"'  },

  // ────────────────────────────────────────────────────────
  // FIRENOCK — verified from firenock.com
  // Aerovane III is 30% lighter than Aerovane II (calculated)
  // ────────────────────────────────────────────────────────
  { name: 'Firenock Aerovane III',     weight: 5.18, length: '2.4"'  },  // VERIFIED (10mm height, 30% lighter than II)
  { name: 'Firenock Aerovane II',      weight: 6.48, length: '2.4"'  },  // calculated from III ratio
  { name: 'Firenock Aerovane (original)', weight: 8.0, length: '2.5"' },
  { name: 'Firenock AeroWeave Vane',   weight: 6.0,  length: '2.0"'  },

  // ────────────────────────────────────────────────────────
  // VANETEC
  // ────────────────────────────────────────────────────────
  { name: 'Vanetec V-Max 2.0"',        weight: 6.5,  length: '2.0"' },
  { name: 'Vanetec V-Max 2.3"',        weight: 7.5,  length: '2.3"' },
  { name: 'Vanetec Super Spine 3"',    weight: 7.5,  length: '3.0"' },
  { name: 'Vanetec Swift 2"',          weight: 6.0,  length: '2.0"' },
  { name: 'Vanetec HP 2.3"',           weight: 6.5,  length: '2.3"' },

  // ────────────────────────────────────────────────────────
  // OLYMPIC / SPIN VANES (target)
  // ────────────────────────────────────────────────────────
  { name: 'Beiter Spin Vane',          weight: 3.0,  length: '2.0"'  },
  { name: 'XS Wings 60mm',             weight: 2.0,  length: '2.4"'  },
  { name: 'XS Wings 70mm',             weight: 2.5,  length: '2.75"' },
  { name: 'Spider Speedflex 1.75"',    weight: 3.5,  length: '1.75"' },
  { name: 'Norway Spin Wing 1.75"',    weight: 2.0,  length: '1.75"' },
  { name: 'Norway Spin Wing 2"',       weight: 2.5,  length: '2.0"'  },
  { name: 'Kurly Vanes 1.75"',         weight: 4.0,  length: '1.75"' },
  { name: 'Flex-Fletch FFP 187',       weight: 3.1,  length: '1.87"' },
  { name: 'Flex-Fletch FFP 200',       weight: 3.75, length: '2.0"'  },

  // ────────────────────────────────────────────────────────
  // FEATHERS — Traditional
  // ────────────────────────────────────────────────────────
  { name: 'Gateway Feather Parabolic 5"',    weight: 3.63, length: '5.0"' },
  { name: 'Gateway Feather Parabolic 4"',    weight: 2.76, length: '4.0"' },
  { name: 'Gateway Feather Shield 5"',       weight: 4.1, length: '5.0"' },
  { name: 'Gateway Rayzr 4"',                weight: 3.0, length: '4.0"' },
  { name: 'Trueflight Feather 5"',           weight: 4.0, length: '5.0"' },
  { name: 'Trueflight Feather 4"',           weight: 3.2, length: '4.0"' },
  { name: 'Trueflight Shield Cut 5"',        weight: 4.2, length: '5.0"' },
  { name: 'Trad Vane Feather 5.5"',          weight: 5.0, length: '5.5"' },

  // ────────────────────────────────────────────────────────
  // SPECIALTY / OTHER
  // ────────────────────────────────────────────────────────
  { name: 'Carbon Express CX Vane 2"',       weight: 6.0, length: '2.0"' },
  { name: 'Black Eagle Hunter Vane 2.1"',    weight: 6.5, length: '2.1"' },
  { name: 'Spider 1.5"',                     weight: 1.4, length: '1.5"' },
  { name: 'Mojo Bonecollector 2.6"',         weight: 7.0, length: '2.6"' },

  // ───────────────────────────────────────────────────────────────────
  // 2026-05-31 audit — gap-fill vs Lancaster + Podium
  // AAE EP-16 (common entry-level target vane), Bohning Alpha,
  // Easton Diamond 175/235/280 (we had only 3" and 4"), Gateway feather
  // sizes, Spider 1.8" + Brady, Trueflight feathers, NAP QuikSpin
  // ───────────────────────────────────────────────────────────────────
  { name: 'AAE Plastifletch EP-16',          weight: 3.2, length: '1.75"' },
  { name: 'Bohning Alpha Vane',              weight: 8.5, length: '2.8"' },
  { name: 'Easton Diamond 175',              weight: 3.0, length: '1.75"' },
  { name: 'Easton Diamond 235',              weight: 4.0, length: '2.375"' },
  { name: 'Easton Diamond 280',              weight: 6.0, length: '2.875"' },
  { name: 'Gateway Feather Parabolic 3"',    weight: 2.04, length: '3.0"' },
  { name: 'Gateway Feather Parabolic 2.5"',  weight: 0.80, length: '2.5"' },
  { name: 'Spider 1.8" Medium',              weight: 1.0, length: '1.8"' },
  { name: 'Spider Brady 1.5"',               weight: 1.4, length: '1.5"' },
  { name: 'Trueflight Shield Cut 3"',        weight: 2.0, length: '3.0"' },
  { name: 'Trueflight Shield Cut 4"',        weight: 2.3, length: '4.0"' },
  { name: 'Trueflight Parabolic 4"',         weight: 2.3, length: '4.0"' },
  { name: 'NAP QuikSpin 2.0"',               weight: 11, length: '2.0"' },

  // ───────────────────────────────────────────────────────────────────
  // 2026-07 audit — 1Arrow Archery (1arrowarchery.com), a fletching-only
  // company. Slip-on, jig-less/glue-less stretchable vanes trademarked
  // 1Vane™ / 1Wing™. Only two sizes exist (Micro: 5.5mm opening, fits
  // shafts ≤6.9mm OD; Standard: 7.0mm opening, fits ≤9.5mm OD). The maker
  // publishes NO per-vane grain weight and NO vane length — the only
  // published dimension is the opening diameter (a fit spec, not length).
  // Per-vane grain weights VERIFIED 2026-06-15 direct from the 1Arrow Archery
  // owner (their FAQ page publishes wider ranges that don't match actual
  // production weights): 1Vane Std 23gr / Micro 24gr (Micro heavier — tighter
  // fit = more base material); 1Wing Std 23gr / Micro 22gr. length carries
  // the size label rather than an inch value we don't have. Arrow Wraps
  // product intentionally skipped — vanes.ts has no wraps concept.
  // Appended at the array tail; existing indices unchanged (permalink-safe).
  // ───────────────────────────────────────────────────────────────────
  { name: '1Arrow 1Vane (Standard)',         weight: 23, length: 'Standard' },  // opening 7.0mm, fits shafts ≤9.5mm OD; verified 2026-06-15 direct from 1Arrow Archery owner
  { name: '1Arrow 1Vane (Micro)',            weight: 24, length: 'Micro'    },  // opening 5.5mm, fits shafts ≤6.9mm OD; verified 2026-06-15 direct from 1Arrow Archery owner (Micro heavier — tighter fit = more base material)
  { name: '1Arrow 1Wing (Standard)',         weight: 23, length: 'Standard' },  // opening 7.0mm, fits shafts ≤9.5mm OD; verified 2026-06-15 direct from 1Arrow Archery owner
  { name: '1Arrow 1Wing (Micro)',            weight: 22, length: 'Micro'    },  // opening 5.5mm, fits shafts ≤6.9mm OD; verified 2026-06-15 direct from 1Arrow Archery owner
] as const;
